/**
 * Snackbar Component
 * 
 * Bottom notification bar for persistent messages
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react'
import './Snackbar.css'

export type SnackbarType = 'default' | 'success' | 'error' | 'warning' | 'info'

interface SnackbarAction {
  label: string
  onClick: () => void
}

interface Snackbar {
  id: string
  message: string
  type: SnackbarType
  action?: SnackbarAction
  duration?: number
  dismissible?: boolean
}

interface SnackbarContextType {
  showSnackbar: (message: string, options?: Partial<Omit<Snackbar, 'id' | 'message'>>) => void
  hideSnackbar: () => void
}

const SnackbarContext = createContext<SnackbarContextType | null>(null)

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<Snackbar | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  const showSnackbar = useCallback((
    message: string, 
    options: Partial<Omit<Snackbar, 'id' | 'message'>> = {}
  ) => {
    const newSnackbar: Snackbar = {
      id: `snack-${Date.now()}`,
      message,
      type: options.type || 'default',
      action: options.action,
      duration: options.duration || 4000,
      dismissible: options.dismissible !== false
    }

    setSnackbar(newSnackbar)
    setIsVisible(true)

    // Auto dismiss
    if (newSnackbar.duration && newSnackbar.duration > 0) {
      setTimeout(() => {
        setIsVisible(false)
        setTimeout(() => setSnackbar(null), 300)
      }, newSnackbar.duration)
    }
  }, [])

  const hideSnackbar = useCallback(() => {
    setIsVisible(false)
    setTimeout(() => setSnackbar(null), 300)
  }, [])

  const icons: Record<SnackbarType, string> = {
    default: '',
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  }

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      
      {snackbar && (
        <div className={`snackbar snackbar-${snackbar.type} ${isVisible ? 'snackbar-visible' : ''}`}>
          {icons[snackbar.type] && (
            <span className="snackbar-icon">{icons[snackbar.type]}</span>
          )}
          
          <span className="snackbar-message">{snackbar.message}</span>
          
          {snackbar.action && (
            <button 
              className="snackbar-action"
              onClick={() => {
                snackbar.action?.onClick()
                hideSnackbar()
              }}
            >
              {snackbar.action.label}
            </button>
          )}
          
          {snackbar.dismissible && (
            <button className="snackbar-dismiss" onClick={hideSnackbar}>
              ✕
            </button>
          )}
        </div>
      )}
    </SnackbarContext.Provider>
  )
}

export function useSnackbar(): SnackbarContextType {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}

export default SnackbarProvider